<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {ref} from "vue";
import {onClickOutside} from "@vueuse/core";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import Swal from 'sweetalert2';
import BaseModal from "@/components/global/modals/BaseModal.vue";

const backlinkToAdd = ref('');
const showInput = ref(false);
const addBacklinkButton = ref(null);
const modalOpen = ref(false);
const errorMessage = ref('');

const clear = () => {
  backlinkToAdd.value = '';
}

onClickOutside(addBacklinkButton, () => {
  if (backlinkToAdd.value === null || backlinkToAdd.value === '') {
    showInput.value = false;
  }
});

const queryClient = useQueryClient();
const {mutate: createBacklink, isPending} = useMutation({
  mutationFn: async () =>  await axios.post(`/ajax/v2/backlinks/store`, {
    backlink_url: backlinkToAdd.value,
  }),
  onSuccess: ({data}) => {
    queryClient.invalidateQueries(['backlinks', {type: 'live'}])
    Swal.fire({
      icon: 'success',
      title: 'Backlink Added',
      text: data.message ?? 'Backlink opportunity has been marked as built.',
    });
  },
  onError: ({response}) => {
    errorMessage.value = 'Could not add backlink,<br>please try again later.';
    const {data} = response;
    const {errors} = data;

    if (errors) {
      const fieldErrors = errors[Object.keys(errors)[0]]
      errorMessage.value = fieldErrors[0] ?? errorMessage
    }

    modalOpen.value = true;
  }
})
</script>

<template>
  <DefaultButton
    v-if="!showInput"
    class="tw-px-7 tw-py-3.5 tw-w-56"
    @click="showInput = true"
  >
    <svg-vue
      icon="icons/link"
      class="tw-w-5 tw-h-5 tw-mr-2"
    />
    <span class="tw-font-semibold tw-antialiased">Add Backlink</span>
  </DefaultButton>

  <div
    v-else
    ref="addBacklinkButton"
    class="tw-flex tw-self-stretch tw-w-full tw-max-w-[384px]"
  >
    <label
      class="tw-border-2 tw-border-grey-mid tw-rounded-l-lg tw-border-r-0 tw-flex tw-text-black tw-col-span-12 tw-self-stretch tw-w-full focus-within:tw-border-blue"
    >
      <input
        v-model="backlinkToAdd"
        placeholder="Enter New Backlink"
        class="tw-border-0 tw-rounded-l-lg tw-pl-3 tw-text-lg tw-pr-2 tw-w-full"
        type="text"
      >
      <svg-vue
        v-if="backlinkToAdd"
        icon="icons/search-close"
        class="tw-w-7 tw-h-7 tw-my-2 tw-mr-2 tw-text-grey tw-cursor-pointer"
        @click="clear"
      />
    </label>
    <DefaultButton
      class="tw-self-stretch tw-w-16 !tw-px-0 tw-border-2 tw-border-blue tw-rounded-r-lg !tw-rounded-l-none tw-text-white"
      :disabled="isPending"
      @click="createBacklink"
    >
      <svg-vue
        v-if="isPending"
        icon="icons/loading"
        class="tw-w-5 tw-h-5"
      />

      <svg-vue
        v-else
        icon="icons/plus"
        class="tw-w-5 tw-h-5"
      />
    </DefaultButton>
  </div>

  <Teleport to=".tailwind">
    <BaseModal
      v-if="modalOpen"
      modal-class="tw-max-w-[800px]"
      @close="modalOpen = false"
    >
      <div class="tw-px-16 tw-pb-8 tw-flex tw-flex-col tw-gap-8 tw-items-center">
        <svg-vue
          icon="icons/backlink-failed"
          class="tw-w-20 tw-h-20 tw-text-blue"
        />

        <h2 class="tw-text-2xl tw-text-center tw-font-bold tw-text-grey-dark">
          Add Backlink failed
        </h2>
        <div class="tw-mt-4 tw-mb-9">
          <p
            class="tw-text-base tw-font-grey-dark"
            v-html="errorMessage"
          />
        </div>
        <div>
          <DefaultButton
            @click="modalOpen = false"
          >
            Back
          </DefaultButton>
        </div>
      </div>
    </BaseModal>
  </Teleport>
</template>
